import { Button, Loading } from "@/components";
import { FormSelectMultiple } from "@/components/Form";
import { useAddMember, useGetChat, useGetListChatMember } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AddMemberFormData,
  AddMemberSchema,
} from "../../schemas/AddMemberSchema";

interface Props {
  onClose(): void;
  roomId: number;
}

const FormAddMember: React.FC<Props> = ({ onClose, roomId }) => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {},
    resolver: zodResolver(AddMemberSchema),
  });

  const { chat, isLoading: isLoadingChat } = useGetChat(roomId);
  // Set initial form values
  useEffect(() => {
    if (!chat) return;
    const chatOwnerId = chat.created_by?.id;
    const chatPicId = chat.pic?.id;

    // List viewer options excluding owner and PIC
    const users = chat.chatUsers
      .filter(
        (userRelation) =>
          userRelation.user_id !== chatOwnerId &&
          userRelation.user_id !== chatPicId
      )
      .map((userRelation) => ({
        label: userRelation.user?.full_name,
        value: userRelation.user?.id,
        description: userRelation.user?.email,
      }));

    reset({
      users,
    });
  }, [chat]);

  const { addMember, isAddingMember } = useAddMember();

  // Submit form
  const addMembers = (data: AddMemberFormData) => {
    // Call API to add members
    addMember({
      chatId: roomId,
      userIds: data.users.map((user) => user.value),
    }).then(() => {
      // Close modal
      onClose();
    });
  };

  // State to search in list members
  const [search, setSearch] = useState<string>("");

  // Load list user to select
  const { members, isLoading, isPending, hasNextPage, fetchNextPage } =
    useGetListChatMember(roomId, { s: search, search });

  // parse users to options
  const userOptions = useMemo(() => {
    return members.map((member) => ({
      label: member.user_name,
      value: member.user_id,
      description: member.user_email,
    }));
  }, [members]);

  if (isLoadingChat) return <Loading />;

  return (
    <div>
      <form onSubmit={handleSubmit(addMembers)}>
        <FormSelectMultiple
          control={control}
          name="users"
          options={userOptions}
          label="メンバー名"
          placeholder="メンバーを選択してください。"
          required
          onLoadMoreItems={() => fetchNextPage()}
          isLoadingMoreItems={isLoading || isPending}
          stopCheckScrollToBottom={!hasNextPage}
          onSearchInputChange={setSearch}
          enableOptionDescription
          allowSearchInput
        />

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button
            variant="outlined"
            buttonType="text"
            onClick={onClose}
            className="text-gray-500"
          >
            キャンセル
          </Button>
          <Button
            variant="solid"
            color="primary"
            className="ml-2"
            type="submit"
            isLoading={isAddingMember}
          >
            追加
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FormAddMember;
